const _ = require('lodash');
const CardTypeRef = require('@/lib/PaymentMethods/CardTypeRef');
const CloverPaymentAndRefundVoidReason = require('@/lib/PaymentMethods/Clover/Enums/CloverPaymentAndRefundVoidReason');
const CloverPaymentIssueState = require('@/lib/PaymentMethods/Clover/Enums/CloverPaymentIssueState');
const CloverRefundTransaction = require('@/lib/PaymentMethods/Clover/CloverRefundTransaction');
const CloverTransaction = require('@/lib/PaymentMethods/Clover/CloverTransaction');
const CloverTransactionClassName = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionClassName');
const CloverTransactionEndState = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionEndState');
const CloverTransactionRefundRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverTransactionRefundRequestRecord');
const CloverTransactionState = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionState');
const CloverTransactionType = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionType');
const CloverVoidTransaction = require('@/lib/PaymentMethods/Clover/CloverVoidTransaction');
const EventNames = require('@/lib/Events/EventNames');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PrivilegeNames = require('@/lib/Privileges/PrivilegeNames');
const RefundData = require('@/lib/PaymentMethods/Clover/RefundData');
const TransactionInProgress = require('@/lib/Payments/TransactionInProgress');
const { cloneDeep } = require('@/functions/clone');
const { formatCurrency, formatMoney } = require('@/lib/Formatting/currency');
const { fromMinorUnit, toMinorUnit } = require('@/functions/math');
const { getRefundablePayments } = require('@/libraries/DigiTickets/Payment/refundablePayments');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');
const { ModalCancelledError } = require('@/vue/modals/ModalCancelledError');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');
const { updateAngularUi } = require('@/functions/angular');

/**
 * This is the controller for the inner part of the payment modal for the payment method of "Clover Chip And Pin".
 *
 * @param $filter
 * @param $scope
 * @param $timeout
 * @param {CartService} cartService
 * @param {ConnectivityChecker} connectivityChecker
 * @param {CurrentDevice} currentDevice
 * @param {CurrencyService} currencyService
 * @param {DialogService} dialogService
 * @param {CloverDriver} cloverDriver
 * @param {LangService} langService
 * @param {PaymentMethodService} paymentMethodService
 * @param {PaymentState} paymentState
 * @param {UserService} userService
 * @param {VueModalFactory} vueModalFactory
 */
const CloverPaymentCtrl = function CloverPaymentCtrl(
    $filter,
    $scope,
    $timeout,
    cartService,
    connectivityChecker,
    currentDevice,
    currencyService,
    dialogService,
    cloverDriver,
    langService,
    paymentMethodService,
    paymentState,
    userService,
    vueModalFactory,
) {
    $scope.paymentMethodScopes[cloverDriver.paymentMethodRef] = $scope;

    $scope.cart = cartService;
    $scope.cloverDriver = cloverDriver;
    $scope.paymentState = paymentState;
    $scope.userService = userService;

    $scope.thisPaymentMethodRef = cloverDriver.paymentMethodRef;

    /**
     * @type {PaymentMethod}
     */
    $scope.thisPaymentMethod = paymentMethodService.getByRef($scope.thisPaymentMethodRef);

    /**
     * Has the Clover driver been set up and is ready to use?
     *
     * @type {bool|null}
     */
    $scope.ready = null;

    /**
     * Over-ride the show waiting message.
     *
     * @type {boolean}
     */
    $scope.showWaiting = false;

    /**
     * Has the transaction just been reset e.g. after a manual reconciliation.
     *
     * @type {boolean}
     */
    $scope.isReset = false;

    /**
     * @type {CloverTransaction|null}
     */
    $scope.currentTransaction = null;

    $scope.errorMessage = null;

    /**
     * Is the reload button enabled when there is an errorMessage?
     *
     * @type {boolean}
     */
    $scope.enableReloadButton = false;

    /**
     * Should we show the button that opens the modal that show refundable payments?
     *
     * @type {boolean}
     */
    $scope.showRefundablePaymentsBtn = false;

    /**
     * Control the visibility of the Terminal Screen and Buttons. We want to hide these when a refundable payment is
     * available because we always want to refund in preference to creating Credit Payment.
     *
     * @type {boolean}
     */
    $scope.showTerminalSection = true;

    /**
     * An array of existing payments that are potentially refundable.
     *
     * @type {Payment[]}
     */
    $scope.refundablePayments = [];

    /**
     * An array of refund payments that refund or partially refund an existing payment in refundablePayments.
     *
     * @type {Payment[]}
     */
    $scope.refundPayments = [];

    /**
     * Possible state of start / restart button.
     *
     * @type {Readonly<{RESTART: string, START: string, NO_BUTTON: string}>}
     */
    $scope.startRestartButtonStates = Object.freeze({
        NO_BUTTON: 'NO_BUTTON',
        RESTART: 'RESTART',
        START: 'START',
    });

    /**
     * The current start / restart button state. This can also be used to determine the state of the secondary buttons.
     * For example, we can only carry out an action if there isn't a transaction in progress and
     * either button is showing.
     *
     * @type {string}
     */
    $scope.startRestartButtonState = $scope.startRestartButtonStates.NO_BUTTON;

    /**
     * Is the payment in the process of being added to the cart?
     * This flag is used to help control button visibility in the period when the transaction is completed but has
     * still not been saved to the cart.
     *
     * @type {boolean}
     */
    $scope.addingPayment = false;

    /**
     * Object to hold a css class and user facing status message.
     *
     * @type {{text: null, class: null}}
     */
    $scope.transactionDisplayMessage = {
        class: null,
        text: null,
    };

    $scope.isOnline = connectivityChecker.online;
    connectivityChecker.addObserver({
        key: 'CloverPaymentCtrl',
        notifyOnline: function notifyOnline() {
            if (!$scope.isOnline) {
                if ($scope.currentTransaction) {
                    console.log('ProPoint is back online. Re-submitting Clover transaction');
                    // We can do this as many times as we want because we are sending the idempotency key and so will
                    // get the result of an existing transaction rather than starting a new one.
                    $scope.recoverTransaction(
                        $scope.currentTransaction
                    );
                }
            }
            $scope.ready = true;
            $scope.isOnline = true;
            updateAngularUi();
        },
        notifyOffline: function notifyOffline() {
            $scope.isOnline = false;
            updateAngularUi();
        },
    });

    // This function is repeatedly called to check the status of a transaction.
    $scope.isTransactionInProgress = function isTransactionInProgress() {
        if ($scope.currentTransaction) {
            $scope.checkTransactionInProgress();
        }
        return $scope.currentTransaction && !$scope.currentTransaction.isFinished;
    };

    $scope.checkTransactionInProgress = async function checkTransactionInProgress() {
        // Now handle the transactionResults
        if (!$scope.currentTransaction.isFinished) {
            $scope.currentTransaction.transactionResult.class = 'message-line-danger';
            switch ($scope.currentTransaction.transactionResult.message) {
                case CloverTransactionEndState.SUCCESS:
                case CloverTransactionEndState.REFUNDED:
                    // The payment has been taken and any issues handled.
                    // We offer to print card receipts from the ChangeCtrl (except for void receipts).
                    $scope.currentTransaction.isFinished = true;
                    paymentState.clearPaymentModalCancelable();
                    await cartService.clearTransactionInProgress();
                    $scope.currentTransaction.transactionResult.class = 'message-line-success';
                    $scope.addingPayment = true;

                    // Make sure that we don't automatically close the modal if there is message to display.
                    // e.g. signature verification timeout
                    if ($scope.currentTransaction.userMessage) {
                        $scope.preventClose();
                    }

                    // We can now save the payment.
                    $scope.handleApprovedResponse($scope.currentTransaction);
                    break;
                case CloverTransactionEndState.CANCELLED:
                    // No payment has been taken.
                    // The transaction was cancelled before payment was taken either for ProPoint or the terminal.
                    $scope.currentTransaction.isFinished = true;
                    paymentState.clearPaymentModalCancelable();
                    await cartService.clearTransactionInProgress();
                    await cloverDriver.displayWelcome();
                    updateAngularUi();
                    break;
                case CloverTransactionEndState.ERROR:
                    // No payment has been taken.
                    $scope.currentTransaction.isFinished = true;
                    paymentState.clearPaymentModalCancelable();
                    await cartService.clearTransactionInProgress();
                    await cloverDriver.displayWelcome();
                    updateAngularUi();
                    break;
                case CloverTransactionEndState.VOID:
                    // A payment was taken and then voided either because of a late cancel or as a result of an issue.
                    $scope.currentTransaction.isFinished = true;
                    $scope.currentTransaction.userMessage = langService.getText('CLOVER.VOID_MESSAGE');
                    await $scope.promptForVoidReceipt();
                    $scope.$parent.setVoidOrRefundInProgress(null);
                    await cloverDriver.displayWelcome();
                    paymentState.clearPaymentModalCancelable();
                    await cartService.clearTransactionInProgress();
                    updateAngularUi();
                    break;
                case CloverTransactionEndState.VOID_FAILED:
                    // The payment was taken, but we were not able to void the transaction.
                    // We will have to try and refund later.
                    $scope.currentTransaction.isFinished = true;
                    $scope.currentTransaction.userMessage = langService.getText('CLOVER.VOID_FAILED_MESSAGE');
                    await cloverDriver.displayWelcome();
                    updateAngularUi();
                    break;
                case CloverTransactionEndState.REFUND_FAILED:
                    // We were unable to refund the payment. We may be able to try again later.
                    $scope.currentTransaction.isFinished = true;
                    $scope.currentTransaction.userMessage = langService.getText('CLOVER.REFUND_FAILED_MESSAGE');
                    await cloverDriver.displayWelcome();
                    updateAngularUi();
                    break;
                default:
                    // We have a transaction response with issues that the operator needs to deal with and then either
                    // approve or void the payment.
                    await $scope.checkPartialPaymentReceived();
                    await $scope.checkSignature();
                    await $scope.checkDuplicatePayment();

                    if ($scope.currentTransaction.transactionResult.message) {
                        console.log('Clover isFinished() - unexpected final state');
                        $scope.currentTransaction.isFinished = true;
                        paymentState.clearPaymentModalCancelable();
                        await cartService.clearTransactionInProgress();
                        break;
                    }
            }
        }
    };

    // There were insufficient funds in the customers account so a partial payment was taken.
    // We could create a split payment using another card for the balance, but instead we will just void automatically.
    $scope.checkPartialPaymentReceived = async function checkPartialPaymentReceived() {
        if ($scope.currentTransaction.paymentIssuePartialCheck !== CloverPaymentIssueState.REQUIRED) {
            return;
        }
        $scope.currentTransaction.paymentIssuePartialCheck = CloverPaymentIssueState.IN_PROGRESS;
        $scope.currentTransaction.userMessage = 'CLOVER.INSUFFICIENT_FUNDS';
        await cloverDriver.voidTransaction(
            $scope.currentTransaction,
            CloverPaymentAndRefundVoidReason.DEVELOPER_PAY_PARTIAL_AUTH,
            $scope.currentTransaction.latestResponse.data.payment.id
        );
    };

    // A potential duplicate payment the operator needs to accept or decline and void.
    $scope.checkDuplicatePayment = async function checkDuplicatePayment() {
        if ($scope.currentTransaction.paymentIssueDuplicateCheck !== CloverPaymentIssueState.REQUIRED) {
            return;
        }
        $scope.currentTransaction.paymentIssueDuplicateCheck = CloverPaymentIssueState.IN_PROGRESS;
        dialogService
            .confirm(
                'CLOVER.DUPLICATE_PAYMENT_MESSAGE',
                {
                    title: 'CLOVER.DUPLICATE_PAYMENT_TITLE',
                    cancelLabel: 'CLOVER.PAYMENT_REJECT',
                    okLabel: 'CLOVER.PAYMENT_ACCEPT',
                },
            )
            .then(async (accepted) => {
                if (accepted) {
                    $scope.currentTransaction.paymentIssueDuplicateCheck = CloverPaymentIssueState.CHECKED;
                    $scope.currentTransaction.handleSuccessResult();
                    updateAngularUi();
                } else {
                    // Void the payment and then poll until the payment status changes.
                    // When the status changes we call a handler method on the CloverTransaction.
                    await cloverDriver.voidTransaction(
                        $scope.currentTransaction,
                        CloverPaymentAndRefundVoidReason.REJECT_DUPLICATE,
                        $scope.currentTransaction.latestResponse.data.payment.id
                    );
                }
            });
    };

    // Check the signature either on screen or on the receipt then accept or decline and void.
    $scope.checkSignature = async function checkSignature() {
        if ($scope.currentTransaction.paymentIssueSignatureCheck !== CloverPaymentIssueState.REQUIRED) {
            return;
        }
        $scope.currentTransaction.paymentIssueSignatureCheck = CloverPaymentIssueState.IN_PROGRESS;
        let customHtml = null;
        if ($scope.currentTransaction.latestResponse.data.issues.signature.signature) {
            const signature = $scope.currentTransaction.latestResponse.data.issues.signature.signature;
            const img = signature.data;
            const format = signature.format.toLowerCase();
            customHtml = '<p><img alt="" class="mb-1" src="data:image/' + format + ';base64,' + img + '" /></p>';
        }
        dialogService
            .confirm(
                'CLOVER.SIGNATURE_VERIFICATION_MESSAGE',
                {
                    title: 'CLOVER.SIGNATURE_VERIFICATION_TITLE',
                    cancelLabel: 'CLOVER.PAYMENT_REJECT',
                    okLabel: 'CLOVER.PAYMENT_ACCEPT',
                    customHtml,
                },
            )
            .then(async (accepted) => {
                if (accepted) {
                    $scope.currentTransaction.paymentIssueSignatureCheck = CloverPaymentIssueState.CHECKED;
                    $scope.currentTransaction.handleSuccessResult();
                    updateAngularUi();
                } else {
                    // Void the payment and then poll until the payment status changes.
                    // When the status changes we call a handler method on the CloverTransaction.
                    await cloverDriver.voidTransaction(
                        $scope.currentTransaction,
                        CloverPaymentAndRefundVoidReason.REJECT_SIGNATURE,
                        $scope.currentTransaction.latestResponse.data.payment.id
                    );
                }
            });
    };

    // The payment has been voided so check if the customer would like a void receipt.
    $scope.promptForVoidReceipt = async function promptForVoidReceipt() {
        $scope.currentTransaction.promptForVoidReceipt = true;
        return dialogService
            .confirm(
                'CLOVER.PROMPT_FOR_VOID_RECEIPT',
                {
                    cancelLabel: 'DIALOGS.NO_LABEL',
                    okLabel: 'DIALOGS.YES_LABEL',
                },
            )
            .then(async (result) => {
                if (result) {
                    await cloverDriver.generateReceipt(
                        CloverTransactionType.VOID,
                        $scope.currentTransaction.latestResponse.data.payment.id
                    );
                }
                $scope.currentTransaction.promptForVoidReceipt = false;
            });
    };

    $scope.cancelTransactionDebounced = async () => {
        console.log('Clover: Cancel Transaction clicked');
        if ($scope.currentTransaction && !$scope.currentTransaction.isFinished) {
            // This cancels the current operation, but we don't know if it was successful because an HTTP 200 response
            // just means that the cancel request was received successfully.
            await cloverDriver.cancelInFlight($scope.currentTransaction);
        } else {
            // The transaction is either finished or doesn't exist.
            $scope.ready = false;
            await $scope.reset();
        }
    };

    $scope.cancelTransaction = _.debounce($scope.cancelTransactionDebounced, 1000, {
        leading: true,
        trailing: false,
    });

    $scope.cancelTransactionWhenOffline = async () => {
        dialogService
            .confirm(
                'CLOVER.OFFLINE_CANCEL_MESSAGE',
                {
                    cancelLabel: 'CLOSE',
                    cancelColor: 'default',
                    okLabel: 'CLOVER.CANCEL_TRANSACTION',
                    okColor: 'danger',
                },
            )
            .then(async (result) => {
                if (result) {
                    $scope.ready = false;
                    $scope.currentTransaction = null;
                    await $scope.reset();
                    updateAngularUi();
                }
            });
    };

    // This method is triggered by clicking the START or RESTART buttons.
    // After clicking this if autoStartTransactions = true the transaction will start automatically.
    $scope.tryAgainDebounced = async () => {
        console.log('Clover: Start / Restart clicked');
        $scope.clearError();
        await $scope.resetCloverPaymentCtrl();
        await $scope.initiateTransaction($scope.cart.getRemainingBalance());
    };

    // Debounced await with flag to handle extreme cases where ProPoint temporarily freezes.
    let isTryAgainRunning = false;
    $scope.tryAgain = _.debounce(async function () {
        if (isTryAgainRunning) {
            return;
        }
        isTryAgainRunning = true;

        try {
            await $scope.tryAgainDebounced();
        } finally {
            isTryAgainRunning = false;
        }
    }, 1000, {
        leading: true,
        trailing: false,
    });

    $scope.makePartialPayment = () => {
        $scope.showPartialPaymentModal('make', function (amount) {
            // This causes the 'Waiting for payment terminal...' message to display before the new transaction starts.
            $scope.currentTransaction = null;
            $scope.initiateTransaction(amount);
            updateAngularUi();
        });
    };

    $scope.initiateTransaction = async (
        amount,
        cardholderNotPresent = false,
        externalPaymentId = null,
        externalReferenceId = null
    ) => {
        // Prevent closing the payment modal whilst we set up and then submit the transaction.
        // This is cleared in the event of an error.
        paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);
        try {
            const amountInMinorUnits = toMinorUnit(amount);
            console.log('initiateTransaction...', amount);

            if (!$scope.ready) {
                throw new Error('Can\'t start Clover transaction because config is not valid.');
            }

            if (externalPaymentId) {
                console.log('Clover: existing externalPaymentId provided', externalPaymentId);
            } else {
                if ($scope.isTransactionInProgress()) {
                    throw new Error('Transaction already in progress.');
                }
                // If an externalPaymentId is passed in then we are attempting to recover an in progress transaction.
                // Otherwise, we generate the thirdPartyId here.
                externalPaymentId = $scope.cart.generatePaymentThirdPartyID();
                externalReferenceId = $scope.cart.generateShortThirdPartyPaymentRef();

                // Only check if the terminal is available if this is a new payment. If we are resuming a transaction
                // in progress then the terminal is very likely to busy.
                const terminalAvailable = await $scope.isTerminalAvailable();
                if (!terminalAvailable) {
                    return;
                }
            }

            // Ensure these variables are cleared.
            $scope.isReset = false;
            $scope.signatureCheckModalID = null;

            const cloverTransaction = cloverDriver.createTransaction(
                amountInMinorUnits,
                externalPaymentId,
                externalReferenceId
            );

            $scope.currentTransaction = cloverTransaction;
            updateAngularUi();

            // Cashback is enabled in the terminal setup app not by DigiTickets or the API.
            // We can't enable it via the API, but we can disable it via the API.
            // We should disable cashback if the maxCashback amount set in Backoffice = 0.
            cloverTransaction.deviceOptions.disableCashback = userService.company.maxCashback === 0;

            if ($scope.cart.currentCart.containsKitchenItems() && userService.company.gratuityEnabled) {
                cloverTransaction.state = CloverTransactionState.WAITING_FOR_GRATUITY;
                const tipResponse = await cloverDriver.getTipAmount(amountInMinorUnits);
                cloverTransaction.addResponse(tipResponse);
                if (tipResponse.status !== 200) {
                    updateAngularUi();
                    return;
                }
                if (tipResponse.data.response && tipResponse.data.response > 0) {
                    cloverTransaction.setTipAmount(tipResponse.data.response);
                    // We don't want to offer tip + cashback at the same time.
                    // If we do this the response doesn't seem to include the cashback amount but this may just be
                    // an artefact of the test cards.
                    cloverTransaction.setDisableCashback();
                }
                updateAngularUi();
            }

            if (cardholderNotPresent) {
                cloverTransaction.setCardNotPresent();
            }

            cloverDriver.createTransactionRequest(cloverTransaction);
            await $scope.sendTransaction(cloverTransaction);
        } catch (error) {
            $scope.setCloverError(error);
        }
    };

    /**
     * @param {CloverTransaction|CloverRefundTransaction} cloverTransaction
     */
    $scope.sendTransaction = async (cloverTransaction) => {
        // Remember this transaction is in progress.
        await cartService.setTransactionInProgress(
            new TransactionInProgress(
                $scope.thisPaymentMethodRef,
                cloverTransaction.thirdPartyID,
                cloverTransaction.originalAmountInMajorUnits,
                {
                    cloverTransaction: cloneDeep(cloverTransaction),
                }
            )
        );
        await cloverDriver.sendTransaction(cloverTransaction);
        updateAngularUi();
    };

    const normalizeCardType = (cardTypeRef) => {
        cardTypeRef = cardTypeRef.toLowerCase();
        switch (cardTypeRef) {
            case 'mc':
                cardTypeRef = CardTypeRef.MASTERCARD;
                break;
        }
        return cardTypeRef;
    };

    /**
     * @param cloverTransaction
     */
    $scope.handleApprovedResponse = function handleApprovedResponse(cloverTransaction) {
        // Funds received. Add Payment to cart/order.
        let payment = new Payment();
        let amount = null;
        let cardTransaction = null;
        // Amounts from Clover are always a +ve integer in minor units (pence or cents).
        if (cloverTransaction instanceof CloverTransaction) {
            if (cloverTransaction.latestResponse.data.payment) {
                const responseAmount = cloverTransaction.latestResponse.data.payment.amount;
                const originalAmount = cloverTransaction.originalAmountInMinorUnits;
                amount = fromMinorUnit(originalAmount);

                if (cloverTransaction.tipAmountInMinorUnits) {
                    const tipAmount = cloverTransaction.tipAmountInMinorUnits;

                    if (responseAmount - originalAmount - tipAmount !== 0) {
                        throw new Error('Response Amount error (tip)', cloverTransaction);
                    }

                    payment.setGratuity(fromMinorUnit(cloverTransaction.tipAmountInMinorUnits));
                }

                if (cloverTransaction.cashbackAmountInMinorUnits) {
                    payment.setCashback(fromMinorUnit(cloverTransaction.cashbackAmountInMinorUnits));
                }

                payment.setTransactionRef(cloverTransaction.latestResponse.data.payment.id);
                cardTransaction = cloverTransaction.latestResponse.data.payment.cardTransaction;
            }
            if (cloverTransaction.latestResponse.data.credit) {
                amount = fromMinorUnit(-1 * cloverTransaction.latestResponse.data.credit.amount);
                cardTransaction = cloverTransaction.latestResponse.data.credit.cardTransaction;
                payment.setTransactionRef(cloverTransaction.latestResponse.data.credit.id);
            }
        }
        if (cloverTransaction instanceof CloverRefundTransaction) {
            if (cloverTransaction.latestResponse.data.refund) {
                amount = fromMinorUnit(-1 * cloverTransaction.latestResponse.data.refund.amount);
                cardTransaction = cloverTransaction.latestResponse.data.payment.cardTransaction;
                payment.setTransactionRef(cloverTransaction.latestResponse.data.refund.id);
                payment.originalPaymentID = cloverTransaction.originalPaymentID;
            }
        }
        payment.setStatusDetail(cloverDriver.getStatusDetail(cloverTransaction.latestResponse.data));
        payment.setTendered(amount);
        payment.setPaymentMethod($scope.thisPaymentMethod);
        payment.debugInfo = null;
        payment.setThirdPartyID(cloverTransaction.thirdPartyID);

        payment.setLastDigits(cardTransaction.last4);
        payment.setAuthCode(cardTransaction.authCode);
        payment.setCardTypeRef(normalizeCardType(cardTransaction.cardType));

        if (payment.originalPaymentID) {
            // This is refund we have just created.
            $scope.refundPayments.push(payment);
        }

        // This displays the thank-you message on the terminal screen.
        cloverDriver.displayThankYou();
        $scope.addPaymentAndAutoFinish(payment);
        $scope.addingPayment = false;
    };

    $scope.isTerminalAvailable = async function isTerminalAvailable() {
        // Check if the terminal is ready.

        try {
            let terminalAvailable = await cloverDriver.terminalReady();
            if (!terminalAvailable) {
                throw new Error('The terminal is busy. To try again please click the Reload button.');
            }
            return true;
        } catch (error) {
            // This could be a 4xx or 5xx error from the API or just a BUSY which is not an API error but is a problem
            // as far as ProPoint is concerned.
            $scope.ready = false;
            paymentState.clearPaymentModalCancelable();
            $scope.setCloverError(error);
        }

        return false;
    };

    /**
     * @param {string} error
     */
    $scope.handleInvalidConfig = function handleInvalidConfig(error) {
        $scope.ready = false;
        $scope.setCloverError(error);
    };

    $scope.recoverTransaction = async function recoverTransaction(recoveredTransaction) {
        // @see https://docs.clover.com/docs/handling-communication-issues
        // Re-create and re-submit the transactions. Because the transactions are idempotent based on the thirdPartyID
        // this will just pick up where we lost the connection to the Clover server.
        $scope.showWaiting = true;
        try {
            switch (recoveredTransaction.transactionClassName) {
                case CloverTransactionClassName.PAYMENT:
                    await $scope.initiateTransaction(
                        recoveredTransaction.originalAmountInMajorUnits,
                        recoveredTransaction.cardNotPreseent,
                        recoveredTransaction.thirdPartyID,
                        recoveredTransaction.externalReferenceId
                    );
                    break;
                case CloverTransactionClassName.REFUND:
                    // Check the payment to see if the refund has actually completed.
                    // eslint-disable-next-line no-case-declarations
                    const refund = await cloverDriver.checkRefundState(recoveredTransaction);

                    if (!refund) {
                        await $scope.refundPayment(
                            new RefundData(
                                recoveredTransaction.initialRefundData.payment,
                                recoveredTransaction.initialRefundData.refundAmount
                            )
                        );
                    }
                    break;
                case CloverTransactionClassName.VOID:
                    // Don't attempt to do anything. Voids can be re-attempted, and we then simply check on the state
                    // of the payment. If a void is not successful then we can attempt to refund instead.
                    break;
                default:
                    throw new Error('Unrecognised CloverTransactionType', recoveredTransaction);
            }
        } catch (error) {
            paymentState.clearPaymentModalCancelable();
            $scope.currentTransaction = null;
        }
    };

    $scope.setCloverError = function setCloverError(error) {
        $scope.errorMessage = error.message;
        updateAngularUi();
        $timeout(() => {
            $scope.enableReloadButton = true;
        }, 10000);
    };

    // Reset the controller and set the isReset = true.
    // Setting isReset = true  means that autoStartTransactions is ignored and to (re)start the next transaction
    // you have to click either the START or RESTART button.
    $scope.reset = async function reset() {
        await $scope.resetCloverPaymentCtrl();
        $scope.isReset = true;
    };

    // Ensure the controller is in a clean state.
    $scope.resetCloverPaymentCtrl = async function resetCloverPaymentCtrl(clearTransactionInProgressFromCart = true) {
        if (clearTransactionInProgressFromCart) {
            await cartService.clearTransactionInProgress();
        }
        $scope.errorMessage = null;
        $scope.transactionInProgress = false;
        $scope.currentTransaction = null;
        paymentState.clearPaymentModalCancelable();
        $scope.isReset = false;
        $scope.enableReloadButton = false;
    };

    /**
     * Should we show the start button, the restart button, or no button at all?
     *
     * @return {string}
     */
    $scope.showStartRestartButton = function showStartRestartButton() {
        const setState = (state) => {
            $scope.startRestartButtonState = state;
            return state;
        };

        if ($scope.isTransactionInProgress()) {
            return setState($scope.startRestartButtonStates.NO_BUTTON);
        }

        if ($scope.currentTransaction && $scope.currentTransaction.isFinished) {
            switch ($scope.currentTransaction.transactionResult.message) {
                case CloverTransactionEndState.CANCELLED:
                    return setState($scope.startRestartButtonStates.RESTART);
                case CloverTransactionEndState.SUCCESS:
                    if ($scope.cart.getRemainingBalance() !== 0 && !$scope.addingPayment) {
                        // There is a remaining balance after a partial payment.
                        return setState($scope.startRestartButtonStates.START);
                    }
                    return setState($scope.startRestartButtonStates.NO_BUTTON);
                case CloverTransactionEndState.VOID:
                    // They can either cancel completely or try again.
                    if ($scope.currentTransaction.promptForVoidReceipt) {
                        return setState($scope.startRestartButtonStates.NO_BUTTON);
                    }
                    return setState($scope.startRestartButtonStates.START);
                case CloverTransactionEndState.VOID_FAILED:
                    // Maybe refund. Problem we probably can't deal with.
                    return setState($scope.startRestartButtonStates.NO_BUTTON);
                default:
                    // !CANCELLED && !SUCCESSFUL && !TIMED_OUT
                    return setState($scope.startRestartButtonStates.RESTART);
            }
        }

        if ($scope.autoStartCardTransactions) {
            if ($scope.isReset && !cartService.getTransactionInProgress()) {
                // Show the start button even when it would normally be hidden because of the autoStart. This scenario
                // happens after a transaction reset which can happen after a rejected manual reconciliation.
                return setState($scope.startRestartButtonStates.START);
            }
            return setState($scope.startRestartButtonStates.NO_BUTTON);
        }

        if (!$scope.currentTransaction) {
            return setState($scope.startRestartButtonStates.START);
        }
        return setState($scope.startRestartButtonStates.NO_BUTTON);
    };

    $scope.checkShowWaiting = function checkShowWaiting() {
        return !$scope.currentTransaction && !$scope.errorMessage && $scope.showWaiting;
    };

    $scope.showCancelButton = function showCancelButton() {
        if (!$scope.isOnline) {
            return false;
        }
        if ($scope.currentTransaction && $scope.currentTransaction.isFinished === true) {
            // This shows after a VOID
            return false;
        }
        if ($scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON) {
            return true;
        }
        return false;
    };

    $scope.isPartialPaymentButtonDisabled = function isPartialPaymentButtonDisabled() {
        return $scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON;
    };

    $scope.isCancelButtonDisabled = function isCancelButtonDisabled() {
        if (!$scope.currentTransaction) {
            return true;
        }

        const cancelableStates = [
            CloverTransactionState.IN_PROGRESS,
            CloverTransactionState.WAITING_FOR_GRATUITY,
        ];

        if (cancelableStates.includes($scope.currentTransaction.state)) {
            return false;
        }

        return true;
    };

    $scope.isCardNotPresentDisabled = function isCardNotPresentDisabled() {
        if (
            !cloverDriver.config.allowCnp
            || $scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON
        ) {
            return true;
        }
        return false;
    };

    $scope.startCardNotPresentPayment = async function startCardNotPresentPayment() {
        await $scope.initiateTransaction($scope.cart.getRemainingBalance(), true);
    };

    $scope.isManualReconciliationDisabled = function isManualReconciliationDisabled() {
        return $scope.startRestartButtonState === $scope.startRestartButtonStates.NO_BUTTON;
    };

    $scope.startManualReconciliation = () => {
        $scope.privilegesManager.requirePrivilege(PrivilegeNames.MANUAL_PAYMENT_RECONCILIATION).then((result) => {
            if (result.granted) {
                $scope.showManualReconciliation();
            }
        });
    };

    // Migrate this modal to use Vue.
    // @TODO https://digitickets.atlassian.net/browse/PROP-655
    $scope.showManualReconciliation = function showManualReconciliation() {
        let amount = fromMinorUnit($scope.currentTransaction.initialRequest.data.amount);
        if ($scope.currentTransaction.initialRequest.data.transactionType === 'REFUND') {
            amount *= -1;
        }

        // There must be no thousand separator used in the value in the input field.
        const moneyFormattedAmount = formatMoney(amount, 2, '.', '');
        const currencyFormattedAmount = formatCurrency(currencyService, amount);
        dialogService.prompt(
            '',
            {
                customHtml: langService.getText('CLOVER.RECONCILIATION__MESSAGE', { currencyFormattedAmount }),
                inputLabel: 'CLOVER.RECONCILIATION__PAYMENT_ID_LABEL',
                title: 'PAYMENT.MANUAL_RECONCILIATION_TITLE',
                okLabel: 'PAYMENT.MANUAL_RECONCILIATION_OK_BTN',
                okColor: 'success',
                cancelLabel: 'PAYMENT.MANUAL_RECONCILIATION_REJECT_BTN',
                cancelColor: 'danger',
                minLength: 10,
                maxLength: 15,
                keyUp: function (value) {
                    // Only letters and numbers in uppercase.
                    return value ? value.replace(/[^a-z0-9]/gi, '').toUpperCase().trim() : '';
                },
                customInputs: {
                    amount: {
                        value: moneyFormattedAmount,
                        label: 'PAYMENT.MANUAL_RECONCILIATION_AMOUNT',
                    },
                },
            }
        )
            .then((result) => {
                if (result.value) {
                    // Create a payment from the manual info.
                    const payment = new Payment();
                    payment.setTendered(result.amount);
                    payment.setTransactionRef(result.value);
                    payment.setPaymentMethod($scope.thisPaymentMethod);
                    payment.setThirdPartyID($scope.currentTransaction.requestId);
                    $scope.addPaymentAndAutoFinish(payment);
                    $scope.transactionInProgress = false;
                }
            })
            .catch(async (error) => {
                if (error instanceof ModalCancelledError) {
                    $scope.showWaiting = false;
                    await $scope.reset();
                } else {
                    throw error;
                }
            })
            .finally(() => updateAngularUi());
    };

    $scope.$on(
        'payment-modal.void-or-refund-payment-pressed',
        /**
         * @param event
         * @param {Payment} existingPayment
         *
         * @return {Promise<void>}
         */
        function (event, existingPayment) {
            // This listener will be called if the void button is pressed on any payment.
            // Check if it was pressed on a Clover payment. If it was we deal with that here, otherwise ignore
            // it for it is to be dealt with by another listener.
            if (existingPayment?.paymentMethod?.ref === PaymentMethodRef.CARD_CLOVER_CHIP_AND_PIN) {
                voidCloverPayment(existingPayment);
            }
        }
    );

    const voidCloverPayment = async (existingPayment) => {
        if ($scope.cart.getSelectedPaymentMethod() !== $scope.thisPaymentMethod) {
            // If the selected payment method is not already Clover then we need to make it the selected payment
            // method. This serves two purposes:
            // 1. We need the UI to show the status of the VOID.
            // 2. cloverDriver.init() may not have run (for example if the browser was refreshed).
            // If we need to switch then make sure a transaction does not autostart for the balance.
            const originalAutoStartCardTransactionsValue = $scope.autoStartCardTransactions;
            $scope.autoStartCardTransactions = false;
            await $scope.$parent.selectPaymentMethod($scope.thisPaymentMethod);
            $scope.autoStartCardTransactions = originalAutoStartCardTransactionsValue;
        }

        // Prevent closing the payment modal.
        paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);

        try {
            const voidResultHandler = (state) => {
                if (state === CloverTransactionEndState.VOID) {
                    // Call removePayment on PaymentCtrl to remove the payment.
                    $scope.removePayment(existingPayment);
                }
                // If VOID_FAILED the state will show in the card-clover-chip-and-pin.html partial.
                // Note: The voidOrRefundInProgress flag is cleared after receipt printing,
                // in checkTransactionInProgress().
            };

            const voidTransaction = new CloverVoidTransaction(toMinorUnit(existingPayment.amount), voidResultHandler);
            $scope.currentTransaction = voidTransaction;
            updateAngularUi();

            const terminalAvailable = await $scope.isTerminalAvailable();
            if (!terminalAvailable) {
                return;
            }

            $scope.$parent.setVoidOrRefundInProgress(existingPayment.thirdPartyID);
            $scope.cloverDriver.cloverAxiosInstance.setIdempotencyKey(existingPayment.thirdPartyID);

            await $scope.cloverDriver.voidTransaction(
                voidTransaction,
                CloverPaymentAndRefundVoidReason.USER_CANCEL,
                existingPayment.transactionRef
            );
        } catch (e) {
            paymentState.clearPaymentModalCancelable();
            $scope.currentTransaction = null;
            $scope.$parent.setVoidOrRefundInProgress(null);
            $scope.errorMessage = langService.getText('CLOVER.GENERIC_ERROR');
        }
    };

    /**
     * List all the existing payments that are potentially refundable for this order.
     *
     * @returns {Payment[]}
     */
    $scope.getRefundablePayments = function () {
        if (cartService.currentCart.originalOrder) {
            return getRefundablePayments(
                cartService.currentCart.originalOrder.payments,
                [$scope.thisPaymentMethodRef]
            );
        }
        return [];
    };

    $scope.showRefundablePaymentsModal = function () {
        vueModalFactory
            .open(MODAL_NAMES__EPOS.REFUNDABLE_PAYMENTS, {
                payments: $scope.refundablePayments,
                refundPayments: $scope.refundPayments,
                refundNeeded: $scope.cart.getRemainingBalance(),
            })
            .then((refundData) => {
                $scope.refundPayment(refundData);
            })
            .catch(suppressModalCancelledErrorOrReThrow);
    };

    $scope.refundPayment = async function (refundData) {
        paymentState.setPaymentModalCancelable(false, $scope.thisPaymentMethodRef);

        try {
            const refundTransaction = new CloverRefundTransaction(refundData);
            refundTransaction.thirdPartyID = $scope.cart.generatePaymentThirdPartyID();
            refundTransaction.state = CloverTransactionState.ATTEMPTING_TO_REFUND;
            $scope.currentTransaction = refundTransaction;
            updateAngularUi();
            // We need to show the transaction that we have just requested.
            $scope.showTerminalSection = true;
            const terminalAvailable = await $scope.isTerminalAvailable();
            if (!terminalAvailable) {
                return;
            }

            const refundRequest = new CloverTransactionRefundRequestRecord(
                refundTransaction.paymentId,
                refundTransaction.amountInMinorUnits,
                refundTransaction.fullRefund
            );
            refundTransaction.addTransactionRequest(refundRequest);
            await $scope.sendTransaction(refundTransaction);
            updateAngularUi();
        } catch (e) {
            console.error(e);
            paymentState.clearPaymentModalCancelable();
            $scope.currentTransaction = null;
            $scope.errorMessage = langService.getText('CLOVER.GENERIC_ERROR');
        }
    };

    $scope.showTerminalSectionClicked = () => {
        $scope.showTerminalSection = true;
    };

    // If existing unsaved payments are refunds, add them to the refunds array.
    $scope.addExistingRefundPayments = function () {
        if ($scope.cart.payments.length) {
            $scope.cart.payments.forEach((payment) => {
                if (payment.originalPaymentID) {
                    $scope.refundPayments.push(payment);
                }
            });
        }
    };

    $scope.postErrorHandler = () => {
        if ($scope.currentTransaction && !$scope.currentTransaction.isFinished) {
            $scope.recoverTransaction(
                $scope.currentTransaction
            );
            return;
        }
        $scope.reloadCurrentPaymentMethod();
    };

    /**
     * Initialization...
     */
    console.log('Clover Payment Controller Opened');

    $scope.$on(EventNames.PAYMENT_METHOD_CHANGED, async function (event, newPaymentMethod) {
        const newPaymentMethodRef = newPaymentMethod?.ref;
        if (newPaymentMethodRef === $scope.thisPaymentMethodRef) {
            await $scope.resetCloverPaymentCtrl(false);
            console.log('Switched to Clover');

            try {
                $scope.cloverDriver.init();
                $scope.ready = true;
            } catch (error) {
                $scope.ready = false;
                paymentState.clearPaymentModalCancelable();
                $scope.setCloverError(error);
                return;
            }

            $scope.addExistingRefundPayments();

            // Check if there are abandoned transactions we need to resume.
            // This happens (for example) if the browser was refreshed while at the payment stage after the
            const txnInProgress = cartService.getTransactionInProgress();
            if (txnInProgress && txnInProgress.paymentMethodRef === $scope.thisPaymentMethodRef) {
                console.log('There are transactions to recover');
                await $scope.recoverTransaction(txnInProgress.state.cloverTransaction);
            } else {
                if ($scope.cart.getRemainingBalance() < 0) {
                    $scope.refundablePayments = $scope.getRefundablePayments();
                    if ($scope.refundablePayments.length > 0) {
                        $scope.showRefundablePaymentsBtn = true;
                        $scope.showTerminalSection = false;
                    }
                }

                if ($scope.autoStartCardTransactions && !$scope.showRefundablePaymentsBtn) {
                    // Tell the driver to initiate the transaction.
                    console.log('Switched to Clover Chip and Pin method. Start new transaction');
                    await $scope.initiateTransaction($scope.cart.getRemainingBalance());
                    $scope.showWaiting = true;
                } else if ($scope.showRefundablePaymentsBtn) {
                    // Open the refund modal automatically.
                    $scope.showRefundablePaymentsModal();
                    $scope.showWaiting = false;
                } else {
                    // The operator should start the transaction manually.
                    $scope.showWaiting = false;
                }
            }
        } else {
            console.log('Switched away from Payment Clover Chip And Pin');
        }
        updateAngularUi();
    });

    $scope.$on('payment-modal.closing', async () => {
        console.log('CloverPayment modal is closing');
        $scope.currentTransaction = null;
        $scope.addingPayment = false;
        await cartService.clearTransactionInProgress();
    });
};

module.exports = CloverPaymentCtrl;
